import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import surveyService from '../services/surveyService';
import SEOHead from '../components/SEOHead';
import { trackSurveyEvent } from '../components/GoogleAnalytics';

const SurveyList = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await surveyService.getActiveSurveys();
      setSurveys(response.data);
    } catch (error) {
      console.error('Error loading surveys:', error);
      setError('Failed to load surveys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const getSurveyStatus = (survey) => {
    const now = new Date();
    const startDate = survey.startDate ? new Date(survey.startDate) : null;
    const endDate = survey.endDate ? new Date(survey.endDate) : null;

    if (startDate && now < startDate) {
      return { status: 'upcoming', variant: 'info', text: 'Upcoming' };
    }
    if (endDate && now > endDate) {
      return { status: 'expired', variant: 'secondary', text: 'Expired' };
    }
    if (survey.userCompleted) {
      return { status: 'completed', variant: 'success', text: 'Completed' };
    }
    return { status: 'active', variant: 'primary', text: 'Active' };
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading surveys...</p>
      </Container>
    );
  }

  return (
    <>
      <SEOHead 
        title="Available Surveys - MyWebsite"
        description="Participate in engaging surveys and polls. Share your opinions on various topics and see results from our community. New surveys added regularly."
        keywords="surveys, polls, questionnaires, feedback, opinions, participate, community surveys"
      />
      <Container className="mt-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>
              <i className="fas fa-poll me-2 text-primary"></i>
              Available Surveys
            </h1>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {surveys.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="fas fa-clipboard-list fa-4x text-muted mb-3"></i>
                <h4>No Surveys Available</h4>
                <p className="text-muted">
                  There are currently no active surveys available for participation.
                </p>
                <p className="text-muted">
                  Please check back later for new surveys.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {surveys.map(survey => {
                const status = getSurveyStatus(survey);
                const canTakeSurvey = status.status === 'active' && !survey.userCompleted;
                
                return (
                  <Col md={6} lg={4} key={survey.id} className="mb-4">
                    <Card className="h-100 survey-card">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <Badge bg={status.variant}>{status.text}</Badge>
                        <small className="text-muted">
                          {survey.totalQuestions} question{survey.totalQuestions !== 1 ? 's' : ''}
                        </small>
                      </Card.Header>
                      
                      <Card.Body className="d-flex flex-column">
                        <h5 className="card-title">{survey.title}</h5>
                        
                        {survey.description && (
                          <p className="card-text text-muted">
                            {survey.description.length > 150 
                              ? `${survey.description.substring(0, 150)}...`
                              : survey.description
                            }
                          </p>
                        )}

                        <div className="survey-info mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">
                              <i className="fas fa-clock me-1"></i>
                              {survey.questionsPerPage} questions per page
                            </small>
                            {survey.anonymous && (
                              <small className="text-success">
                                <i className="fas fa-user-secret me-1"></i>
                                Anonymous
                              </small>
                            )}
                          </div>

                          {(survey.startDate || survey.endDate) && (
                            <div className="mb-2">
                              {survey.startDate && (
                                <small className="text-muted d-block">
                                  <i className="fas fa-calendar-alt me-1"></i>
                                  Starts: {formatDate(survey.startDate)}
                                </small>
                              )}
                              {survey.endDate && (
                                <small className="text-muted d-block">
                                  <i className="fas fa-calendar-times me-1"></i>
                                  Ends: {formatDate(survey.endDate)}
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </Card.Body>

                      <Card.Footer>
                        {canTakeSurvey ? (
                          <Button 
                            as={Link} 
                            to={`/surveys/${survey.id}/take`}
                            variant="primary" 
                            className="w-100"
                            onClick={() => trackSurveyEvent('start_survey', survey.id, survey.title)}
                          >
                            <i className="fas fa-play me-2"></i>
                            Take Survey
                          </Button>
                        ) : survey.userCompleted || status.status === 'expired' ? (
                          <Button 
                            as={Link} 
                            to={`/surveys/${survey.id}/results`}
                            variant="info" 
                            className="w-100"
                            onClick={() => trackSurveyEvent('view_results', survey.id, survey.title)}
                          >
                            <i className="fas fa-chart-bar me-2"></i>
                            View Results
                          </Button>
                        ) : status.status === 'upcoming' ? (
                          <Button variant="secondary" disabled className="w-100">
                            <i className="fas fa-clock me-2"></i>
                            Not Yet Available
                          </Button>
                        ) : (
                          <Button variant="secondary" disabled className="w-100">
                            <i className="fas fa-times me-2"></i>
                            No Longer Available
                          </Button>
                        )}
                      </Card.Footer>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Col>
      </Row>

      <style jsx>{`
        .survey-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid #dee2e6;
        }
        
        .survey-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .survey-info {
          border-top: 1px solid #f8f9fa;
          padding-top: 1rem;
        }
        
        .card-title {
          color: #495057;
          font-weight: 600;
        }
        
        .card-text {
          line-height: 1.5;
        }
      `}</style>
      </Container>
    </>
  );
};

export default SurveyList;